import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import { getWishlist, removeFromWishlist, addToCart } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { useCart } from '@/lib/CartContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Heart, Trash2, ShoppingBag, Sparkles } from 'lucide-react';

export default function WishlistPage() {
  const { user } = useAuth();
  const { refreshCart } = useCart();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const res = await getWishlist();
        setItems(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const handleRemove = async (collectibleId) => {
    try {
      await removeFromWishlist(collectibleId);
      setItems(prev => prev.filter(item => item.collectible_id !== collectibleId));
      toast.success('Removed from wishlist');
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const handleAddToCart = async (collectibleId) => {
    try {
      await addToCart(collectibleId);
      refreshCart();
      toast.success('Added to chest!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <Navbar />
        <div className="grain-overlay" />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <Heart className="w-16 h-16 text-primary/30 mx-auto mb-6" />
          <h2 className="font-display text-2xl font-bold uppercase mb-4">Login to View Wishlist</h2>
          <p className="text-muted-foreground mb-6">Save your favorite collectibles and track their availability.</p>
          <Link to="/login">
            <Button className="bg-primary text-primary-foreground">Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="wishlist-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-6 h-6 text-primary" />
            <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Wishlist</h1>
          </div>
          <p className="text-muted-foreground">Your saved collectibles</p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <Heart className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <h3 className="font-display text-xl font-bold mb-2">Your wishlist is empty</h3>
            <p className="text-muted-foreground mb-6">Browse our drops and save the pieces you love.</p>
            <Link to="/">
              <Button className="bg-primary text-primary-foreground">
                <Sparkles className="w-4 h-4 mr-2" /> Explore Drops
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="grid gap-4">
            {items.map((item, idx) => (
              <motion.div
                key={item.collectible_id}
                className="bg-card border border-border/40 rounded-sm p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Link to={`/collectible/${item.collectible_id}`} className="flex-shrink-0">
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded bg-muted"
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/collectible/${item.collectible_id}`}>
                    <h3 className="font-display font-bold text-sm hover:text-primary transition-colors">{item.name}</h3>
                  </Link>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge className={item.metal_type === 'gold' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-400/20 text-slate-300'}>
                      {item.metal_type?.toUpperCase()} {item.weight}g
                    </Badge>
                    <Badge variant="outline" className="text-xs">{item.purity}</Badge>
                    {item.quantity_remaining > 0 ? (
                      <span className="text-xs text-green-400">{item.quantity_remaining} in stock</span>
                    ) : (
                      <span className="text-xs text-red-400">Sold out</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Button
                    size="sm"
                    onClick={() => handleAddToCart(item.collectible_id)}
                    disabled={item.quantity_remaining === 0}
                    className="flex-1 sm:flex-none bg-primary text-primary-foreground"
                  >
                    <ShoppingBag className="w-4 h-4 mr-1" /> Add to Chest
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemove(item.collectible_id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
